const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
const SCRIPT_ID = 'cf-turnstile-script';
// Long, because the widget starts solving one click before signup needs the
// token, and slow WebViews need the time. A broken widget rejects earlier,
// through `error-callback`.
const DEFAULT_TIMEOUT_MS = 20000;
/**
 * Matches the backend's `TurnstileValidationError`, so the user reads the same
 * message whether the check failed here or in `PreSignUp`.
 */
export const TURNSTILE_UNAVAILABLE_MESSAGE = "We couldn't verify your request. Please try again.";
let scriptPromise;
let widgetId;
let widgetContainer;
let widgetErrored = false;
let challengeVisible = false;
let waiters = [];
const interactiveListeners = new Set();
const widgetUsable = () => widgetId !== undefined && !widgetErrored;
/**
 * Loads the Cloudflare Turnstile script once and resolves when
 * `window.turnstile` is usable. Modelled on the Peach SDK loader in
 * `src/components/buyCrypto/EFTDepositComponent.vue`; there is no generic
 * useScript composable in this codebase.
 */
export function loadTurnstileScript() {
    if (window.turnstile)
        return Promise.resolve();
    if (scriptPromise)
        return scriptPromise;
    scriptPromise = new Promise((resolve, reject) => {
        const existing = document.getElementById(SCRIPT_ID);
        if (existing) {
            existing.addEventListener('load', () => resolve());
            existing.addEventListener('error', () => reject(new Error('Failed to load Turnstile')));
            return;
        }
        const script = document.createElement('script');
        script.id = SCRIPT_ID;
        // render=explicit so we control when the widget mounts, rather than
        // Turnstile auto-scanning the DOM for .cf-turnstile containers.
        script.src = `${SCRIPT_URL}?render=explicit`;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => {
            scriptPromise = undefined;
            reject(new Error('Failed to load Turnstile'));
        };
        document.head.appendChild(script);
    });
    return scriptPromise;
}
/**
 * Returns the solved token, or an empty string if the widget has not solved
 * yet. Signup uses `waitForTurnstileToken`, which never returns an empty token.
 */
export function getTurnstileToken() {
    if (widgetId === undefined)
        return '';
    return window.turnstile?.getResponse(widgetId) ?? '';
}
function forget(waiter) {
    waiters = waiters.filter(pending => pending !== waiter);
    if (waiter.timer !== undefined)
        clearTimeout(waiter.timer);
}
function armTimeout(waiter) {
    waiter.timer = setTimeout(() => {
        const token = getTurnstileToken();
        forget(waiter);
        // Never return an empty token: the backend rejects one, so a slow challenge
        // becomes a failed signup.
        if (!token) {
            waiter.reject(new Error(TURNSTILE_UNAVAILABLE_MESSAGE));
            return;
        }
        waiter.resolve(token);
    }, waiter.timeoutMs);
}
function settleAll(settle) {
    const pending = waiters;
    waiters = [];
    pending.forEach(waiter => {
        if (waiter.timer !== undefined)
            clearTimeout(waiter.timer);
        settle(waiter);
    });
}
/**
 * The timeout only makes sense for the silent path - a human working through a
 * visible challenge has no useful deadline - so it is dropped while the
 * challenge is on screen and re-armed once Cloudflare hands control back.
 */
function setChallengeVisible(visible) {
    if (challengeVisible === visible)
        return;
    challengeVisible = visible;
    waiters.forEach(waiter => {
        if (visible) {
            if (waiter.timer !== undefined)
                clearTimeout(waiter.timer);
            waiter.timer = undefined;
        }
        else {
            armTimeout(waiter);
        }
    });
    interactiveListeners.forEach(listener => listener(visible));
}
/**
 * Subscribes to Cloudflare showing and hiding an interactive challenge, so the
 * caller can tell the user to solve it rather than leaving them on a spinner.
 * Returns an unsubscribe.
 */
export function onTurnstileInteractive(listener) {
    interactiveListeners.add(listener);
    return () => {
        interactiveListeners.delete(listener);
    };
}
/**
 * Drops the widget and everything tracking it, so the next render starts clean.
 */
export function removeTurnstile() {
    const id = widgetId;
    widgetId = undefined;
    widgetContainer = undefined;
    widgetErrored = false;
    settleAll(waiter => waiter.reject(new Error(TURNSTILE_UNAVAILABLE_MESSAGE)));
    setChallengeVisible(false);
    if (id === undefined)
        return;
    try {
        window.turnstile?.remove(id);
    }
    catch {
        // already gone with its container; the ids are cleared either way
    }
}
/**
 * Renders an invisible/managed Turnstile widget into `container`. Safe to call
 * more than once - a repeat call for the same container reuses the widget.
 */
export async function renderTurnstile(container) {
    const siteKey = process.env.VUE_APP_TURNSTILE_SITE_KEY;
    if (!siteKey) {
        throw new Error('Turnstile site key not configured');
    }
    await loadTurnstileScript();
    // A remount hands us a new container while `widgetId` still points at the
    // detached old one, which can neither solve nor reset - so rebuild instead.
    if (widgetContainer === container)
        return;
    removeTurnstile();
    widgetId = window.turnstile?.render(container, {
        sitekey: siteKey,
        // Draw nothing unless Cloudflare needs an interactive challenge, so the
        // caller's container has to stay in the layout, not be `display: none`.
        appearance: 'interaction-only',
        action: 'signup',
        callback: token => {
            widgetErrored = false;
            settleAll(waiter => waiter.resolve(token));
        },
        // Without this a failing widget (usually the site key's hostname list not
        // covering this deploy) is silent, and signup just stalls until the timeout.
        'error-callback': () => {
            widgetErrored = true;
            settleAll(waiter => waiter.reject(new Error(TURNSTILE_UNAVAILABLE_MESSAGE)));
            setChallengeVisible(false);
        },
        'before-interactive-callback': () => setChallengeVisible(true),
        'after-interactive-callback': () => setChallengeVisible(false),
    });
    widgetContainer = widgetId === undefined ? undefined : container;
}
/**
 * Resolves with a token, or rejects - it never resolves an empty string.
 * `timeoutMs` bounds only the unattended wait; see `setChallengeVisible`.
 */
export function waitForTurnstileToken(timeoutMs = DEFAULT_TIMEOUT_MS) {
    const token = getTurnstileToken();
    if (token)
        return Promise.resolve(token);
    if (!widgetUsable()) {
        return Promise.reject(new Error(TURNSTILE_UNAVAILABLE_MESSAGE));
    }
    return new Promise((resolve, reject) => {
        const waiter = { resolve, reject, timeoutMs };
        waiters.push(waiter);
        if (!challengeVisible)
            armTimeout(waiter);
    });
}
/**
 * Turnstile tokens are single-use and expire after ~300s, so a retry needs a
 * fresh one. Call this after any failed signup attempt.
 */
export function resetTurnstile() {
    if (widgetId === undefined)
        return;
    try {
        window.turnstile?.reset(widgetId);
        // It is solving again, so an earlier failure no longer describes it.
        widgetErrored = false;
        setChallengeVisible(false);
    }
    catch {
        // Cloudflare throws once the container is detached. Callers reset from an
        // error path, so throwing here would swallow the error they were about to
        // show - drop the widget and let the next render build a fresh one.
        removeTurnstile();
    }
}
